import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { FwCardModule, FwAvatarModule, FwBadgeModule } from '@flywheel-io/vision';
import { Annotation, AnnotationMetricGrouped } from '../../../shared/models/annotations.types';
import { SharedModule } from '../../../shared/shared.module';
import { DatePipe, DecimalPipe } from '@angular/common';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { CoreApiService } from '../../../core-api.service';
import { lastValueFrom } from 'rxjs';
import { RouterModule } from '@angular/router';

@Component({
  selector: 'app-annotation-card',
  standalone: true,
  imports: [
    DatePipe,
    DecimalPipe,
    FwCardModule,
    FwAvatarModule,
    FwBadgeModule,
    SharedModule,
    RouterModule,
],
  templateUrl: './annotation-card.component.html',
  styleUrl: './annotation-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnnotationCardComponent {
  coreApi = inject(CoreApiService);
  annotation = input<Annotation>();
  showMore = false;

  authorQuery = injectQuery(() => ({
    enabled: Boolean(this.annotation()?.origin?.id),
    queryKey: ['users', this.annotation()?.origin?.id],
    queryFn: () => lastValueFrom(this.coreApi.users.get(this.annotation()?.origin?.id)),
  }))
  author = this.authorQuery.data;

  formatMeasurement(measurement: AnnotationMetricGrouped): string {
    // measurement comes from annotation.metrics.by_label
    const isTotal = measurement.count > 1 ? 'Total Area:' : '';
    const isArea = measurement.area ? '²' : '';
    const relevantNumber = (measurement.area || measurement.length || measurement.angle);
    let unit = measurement.units?.spatial || measurement.units?.pixel;
    // prefer a measurement group summary if available
    if (relevantNumber) {
      const measurementToDisplay = roundedNumber(relevantNumber);
      return `${isTotal} ${measurementToDisplay} ${unit}${isArea}`.trim();
    }

    if (measurement.count === 1) {
      // if the measurement group has a single element, get that element's non-area metrics
      const element = this.annotation().elements.find(el => el.label === measurement.label);
      unit = element.metrics.units?.spatial || element.metrics.units?.pixel;
      if (!element) {
        return '';
      }
      switch (element.type) {
        case 'Length':
          return `${roundedNumber(element.metrics.length)} ${unit}`;
        case 'Bidirectional':
          return `L: ${roundedNumber(element.metrics.length)} ${unit} W: ${roundedNumber(element.metrics.width)} ${unit}`;
        case 'Angle':
          return `${roundedNumber(element.metrics.angle)} °`;
        case 'Freehand':
          return `${roundedNumber(element.metrics.perimeter)} ${unit}`;
        default:
          return '';
      }
    }
    return '';
  }
}

function roundedNumber(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}
